import type { Column, SortDirection } from '../../claim-history/simple-table'
import type { Todos as TodosType } from '../queries/todos'

import LinkIcon from '@material-design-icons/svg/filled/link.svg?component'
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?component'
import cx from 'classnames'
import Parser from 'html-react-parser'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAppSelector } from '../../../store'
import * as Table from '../../claim-history/simple-table'
import { useTableSort } from '../../claim-history/simple-table/use-sort-hook'
import DueDays from '../../PatientFolder/IGAO/components/DueDays'
import * as Card from '../../shared/card'
import { useTodos } from '../queries/todos'

function RenderClientName ({ todo, clientNames }: { todo: TodosType, clientNames: Record<string, string> }): JSX.Element {
  const client = todo?.clientName
  const name = clientNames[client] ?? client
  return (
    <div
      className='client-column tasks'
      title={name}
    >
      {name}
    </div>
  )
}

function RenderNextAction ({ number, urgent }: { number: string, urgent: boolean }): JSX.Element {
  return (
    <div style={{ display: 'flex' }}>
      <DueDays date={number} />
      {urgent
        ? (
          <div style={{ paddingLeft: '8px' }}>
            <WarningIcon
              style={{ transform: 'scale(0.9)' }}
              fill='#F2545A'
            />
          </div>
        )
        : (
          ''
        )}
    </div>
  )
}

interface Props {
  scope: string
  showFilter?: boolean
}

interface Filters {
  'not-started': boolean
  'in-progress': boolean
  'on-hold': boolean
}

const filterNameMapping: Record<keyof Filters, string> = {
  'not-started': 'New',
  'in-progress': 'Open',
  'on-hold': 'Hold'
}

export function Todos ({ showFilter = false }: Props): JSX.Element {
  const clients = useAppSelector(state => state.globalVars.clients)
  const { sort, setSort } = useTableSort<TodosType>({ field: 'nextDueDate', direction: 'asc' })
  const [ filter, setFilter ] = useState<Filters>({
    'not-started': true,
    'in-progress': true,
    'on-hold': true
  })

  // const filterArray = (Object.keys(filter) as Array<keyof Filters>)?.reduce<string[]>((acc, key) => {
  //   if (filter[key]) {
  //     return [ ...acc, key ]
  //   }
  //   return acc
  // }, [])

  const todosQuery = useTodos({ scope: 'mine' }, sort)

  const clientNames = clients?.reduce<Record<string, string>>((acc, item) => {
    return ({
      ...acc,
      [item.clientName]: item.display
    })
  }, {})

  const columns = useMemo((): Array<Column<TodosType> & {onSort?: (a: TodosType, b: TodosType) => number}> => [
    {
      name: 'patientName',
      header: 'Patient Name',
      getValue: a => (
        <Link
          to={`/patient/${a.clientName}/${a.patientId}/patientFolder`}
        >
          {a?.patientName}
        </Link>
      ),
      isSortable: true
    },
    {
      name: 'domain',
      header: 'Domain'
    },
    {
      name: 'issue',
      header: 'Issue',
      getValue: a => Parser(a?.description || '', { trim: true })
    },
    {
      name: 'nextDueDate',
      header: 'Next Due',
      isSortable: true,
      getValue: a => {
        return (
          <RenderNextAction
            number={a.dueDate}
            urgent={a.urgent}
          />
        )
      }
    },
    {
      name: 'assigneeName',
      header: 'Next Assigned'
    },
    {
      name: 'client',
      header: 'Client',
      isSortable: true,
      getValue: a => (
        <RenderClientName
          clientNames={clientNames ?? {}}
          todo={a}
        />
      ),
      onSort: (a, b) => {
        const aClient = a.clientName
        const bClient = b.clientName
        const aName = clientNames?.[aClient] ?? aClient
        const bName = clientNames?.[bClient] ?? bClient
        if (aName > bName) {
          return 1
        }
        if (aName === bName) {
          return 0
        }
        return -1
      }
    }
  ], [ clientNames ])

  return (
    <Card.Card>
      <Card.Header>
        <div className='w-full flex flex-row justify-between items-center'>
          <Link
            className='flex flex-row gap-1 items-center'
            to='/tasks'
          >
            <h2>Tasks</h2>
            <LinkIcon fill='#222' />
          </Link>

          {showFilter && (
            <div className='flex flex-row gap-4'>
              {(Object.keys(filter) as Array<keyof Filters>).map((key) => (
                <button
                  key={key}
                  className={cx('px-6 py-2 rounded-md transition-colors', filter[key] ? 'bg-primary bg-opacity-10 text-primary' : 'bg-gray-200 text-gray-400')}
                  onClick={() => setFilter((prev) => ({
                    ...prev,
                    [key]: !prev[key]
                  })
                  )}
                  type='button'
                >
                  {filterNameMapping[key]}
                </button>
              ))}
            </div>
          )}

        </div>
      </Card.Header>
      <Table.Table
        expandable={false}
        data={todosQuery.data?.rows ?? []}
        error={todosQuery.error as Error}
        fetchStatus={todosQuery.fetchStatus}
        sort={sort}
        onSortChange={(field: keyof TodosType, direction: SortDirection) => setSort({ field, direction })}
        queryStatus={todosQuery.status}
        noDataPlaceholder='There are no tasks to display'
        columns={columns}
        disableMinHeight
      />
    </Card.Card>
  )
}
